import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, AlertTriangle } from 'lucide-react';

const SecurityCheckpoint = () => {
  const navigate = useNavigate();
  const [securityKey, setSecurityKey] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(false);

  // PERMANENT SECRET PASSPHRASE - NEVER CHANGES
  const SECRET_PASSPHRASE = 'EDGE2024ADMIN';

  const handleVerify = async (e) => {
    e.preventDefault();

    if (locked) {
      alert('Access locked due to multiple failed attempts. Please try again later.');
      return;
    }

    setLoading(true);

    // Simulate security check delay
    setTimeout(async () => {
      if (securityKey === SECRET_PASSPHRASE) {
        // Store security clearance
        sessionStorage.setItem('security_clearance', 'granted');
        sessionStorage.setItem('clearance_timestamp', Date.now().toString());
        
        // Create a special passphrase-based admin session
        // Login with the edgeadmin account automatically
        try {
          const BACKEND_URL = '';
          const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              username: 'edgeadmin',
              password: 'EdgeAdmin2024!'
            })
          });

          if (response.ok) {
            const data = await response.json();
            // Store admin token for API access
            localStorage.setItem('admin_token', data.token);
            localStorage.setItem('admin_user', JSON.stringify(data.admin));
            localStorage.setItem('passphrase_access', 'true');
            
            // Redirect directly to admin dashboard (bypass login screen)
            navigate('/secure-admin-access/dashboard');
          } else {
            alert('System authentication error. Please contact administrator.');
          }
        } catch (error) {
          console.error('Auto-login error:', error);
          alert('Connection error. Please try again.');
        }
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        
        if (newAttempts >= 3) {
          setLocked(true);
          alert('Access denied. Too many failed attempts. System locked.');
        } else {
          alert(`Invalid security key. ${3 - newAttempts} attempts remaining.`);
        }
        setSecurityKey('');
      }
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Security Warning */}
        <div className="mb-8 p-6 bg-red-900/20 border-2 border-red-500/50 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle className="w-8 h-8 text-red-400 animate-pulse" />
            <h2 className="text-red-400 font-bold text-xl">RESTRICTED AREA</h2>
          </div>
          <p className="text-red-300 text-sm">
            Unauthorized access to this system is strictly prohibited and monitored. 
            All access attempts are logged and tracked.
          </p>
        </div>

        {/* Security Checkpoint */}
        <div className="bg-gradient-to-br from-slate-950 to-slate-900 border-2 border-cyan-500/30 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-500 flex items-center justify-center animate-pulse">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-cyan-400 mb-2">Security Checkpoint</h1>
            <p className="text-cyan-600 text-sm">Authorization Required</p>
          </div>

          <form onSubmit={handleVerify} className="space-y-6">
            {/* Security Key Input */}
            <div>
              <label className="block text-cyan-400 text-sm font-semibold mb-2">
                Enter Security Passphrase
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-600" />
                <input
                  type="password"
                  value={securityKey}
                  onChange={(e) => setSecurityKey(e.target.value)}
                  placeholder="••••••••••••••••••"
                  disabled={locked}
                  className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-900 focus:outline-none focus:border-cyan-400 transition-colors disabled:opacity-50 font-mono"
                />
              </div>
            </div>

            {/* Attempts Counter */}
            {attempts > 0 && !locked && (
              <div className="p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
                <p className="text-orange-400 text-sm text-center">
                  ⚠️ Failed attempts: {attempts}/3
                </p>
              </div>
            )}

            {/* Locked Message */}
            {locked && (
              <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm text-center font-semibold">
                  🔒 ACCESS LOCKED - Too many failed attempts
                </p>
              </div>
            )}

            {/* Verify Button */}
            <button
              type="submit"
              disabled={loading || locked || !securityKey}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 hover:from-cyan-400 hover:via-blue-400 hover:to-purple-400 text-white font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Verifying...
                </div>
              ) : (
                <>
                  <Shield className="w-5 h-5 inline mr-2" />
                  Verify Access
                </>
              )}
            </button>
          </form>

          {/* Security Notice */}
          <div className="mt-6 p-4 bg-slate-900/50 border border-cyan-500/20 rounded-lg">
            <p className="text-cyan-600 text-xs text-center">
              🔐 256-bit encrypted connection | IP logged | Activity monitored
            </p>
          </div>
        </div>

        {/* System Info */}
        <div className="mt-6 text-center">
          <p className="text-slate-600 text-xs font-mono">
            System ID: SEC-{Math.random().toString(36).substring(2, 10).toUpperCase()}
          </p>
          <p className="text-slate-700 text-xs font-mono mt-1">
            Session: {new Date().toISOString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SecurityCheckpoint;
